import type { ApiClient } from '@treneo/infrastructure/api';
import { BaseApi } from '@treneo/infrastructure/api';
import type {
  IAcceptTrainerInvitationPublicByTokenResponse,
  IAcceptTrainerInvitationRequest,
  IAcceptTrainerInvitationResponse,
  IGetManagedOrganizationUsersResponse,
  IGetMyManagedOrganizationsResponse,
  IGetMyPendingTrainerInvitationsResponse,
  IGetOrganizationInvitationsResponse,
  IGetOrganizationMembershipHistoryResponse,
  IGetOrganizationTrainersResponse,
  IGetOrganizationUsersResponse,
  IGetSystemInvitationsResponse,
  IGetTrainerInvitationByTokenResponse,
  IInviteTrainerRequest,
  IInviteTrainerResponse,
  IRejectTrainerInvitationRequest,
  IRejectTrainerInvitationResponse,
  IRemoveUserFromOrganizationRequest,
  IRemoveUserFromOrganizationResponse,
  IRevokeTrainerRoleRequest,
  IRevokeTrainerRoleResponse,
  Result
} from '@treneo/models';
import type { PaginationRequest } from '@treneo/shared/utils';
import { toOptionalApiPaginationParams } from '@treneo/shared/utils';

import { ORGANIZATIONS_ENDPOINTS } from './organizations-endpoints.constants';

export class OrganizationsApi extends BaseApi {
  protected readonly baseUrl = '/api';

  constructor(apiClient: ApiClient) {
    super(apiClient);
  }

  getOrganizationTrainers = (
    organizationId: string
  ): Promise<Result<IGetOrganizationTrainersResponse>> =>
    this.get<IGetOrganizationTrainersResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_TRAINERS(organizationId)}`,
      null,
      'organizations.errors.getOrganizationTrainersFailed'
    );

  getOrganizationTrainersByDaDesktopId = (
    daDesktopId: number
  ): Promise<Result<{ trainers: IGetOrganizationTrainersResponse['trainers'] }>> =>
    this.get<{ trainers: IGetOrganizationTrainersResponse['trainers'] }>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_TRAINERS_BY_DADESKTOP_ID(daDesktopId)}`,
      null,
      'organizations.errors.getOrganizationTrainersByDaDesktopIdFailed'
    );

  getOrganizationInvitations = (
    organizationId: string,
    request?: PaginationRequest
  ): Promise<Result<IGetOrganizationInvitationsResponse>> => {
    const params = toOptionalApiPaginationParams(request);
    return this.get<IGetOrganizationInvitationsResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_INVITATIONS(organizationId)}`,
      params,
      'organizations.errors.getOrganizationInvitationsFailed'
    );
  };

  getSystemInvitations = (request: {
    page: number;
    pageSize: number;
  }): Promise<Result<IGetSystemInvitationsResponse>> =>
    this.get<IGetSystemInvitationsResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.SYSTEM_INVITATIONS}`,
      { page: request.page, pageSize: request.pageSize },
      'organizations.errors.getSystemInvitationsFailed'
    );

  getTrainerInvitationByToken = (
    token: string
  ): Promise<Result<IGetTrainerInvitationByTokenResponse & { organizationId: string }>> =>
    this.get<IGetTrainerInvitationByTokenResponse & { organizationId: string }>(
      `/${ORGANIZATIONS_ENDPOINTS.INVITATION_BY_TOKEN(token)}`,
      null,
      'organizations.errors.getTrainerInvitationFailed'
    );

  getMyPendingTrainerInvitations = (): Promise<Result<IGetMyPendingTrainerInvitationsResponse>> =>
    this.get<IGetMyPendingTrainerInvitationsResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.MY_PENDING_TRAINER_INVITATIONS}`,
      null,
      'organizations.errors.getMyPendingTrainerInvitationsFailed'
    );

  getOrganizationUsers = (
    organizationId: string,
    request?: PaginationRequest
  ): Promise<Result<IGetOrganizationUsersResponse>> => {
    const params = toOptionalApiPaginationParams(request);
    return this.get<IGetOrganizationUsersResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_USERS(organizationId)}`,
      params,
      'organizations.errors.getOrganizationUsersFailed'
    );
  };

  getOrganizationMembershipHistory = (
    organizationId: string,
    request?: PaginationRequest
  ): Promise<Result<IGetOrganizationMembershipHistoryResponse>> => {
    const params = toOptionalApiPaginationParams(request);
    return this.get<IGetOrganizationMembershipHistoryResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_USERS_HISTORY(organizationId)}`,
      params,
      'organizations.errors.getOrganizationMembershipHistoryFailed'
    );
  };

  getMyManagedOrganizations = (
    request?: PaginationRequest
  ): Promise<Result<IGetMyManagedOrganizationsResponse>> => {
    const params = toOptionalApiPaginationParams(request);
    return this.get<IGetMyManagedOrganizationsResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.MY_MANAGED_ORGANIZATIONS}`,
      params,
      'organizations.errors.getMyManagedOrganizationsFailed'
    );
  };

  getManagedOrganizationUsers = (
    request?: PaginationRequest
  ): Promise<Result<IGetManagedOrganizationUsersResponse>> => {
    const params = toOptionalApiPaginationParams(request);
    return this.get<IGetManagedOrganizationUsersResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.MANAGED_ORGANIZATION_USERS}`,
      params,
      'organizations.errors.getManagedOrganizationUsersFailed'
    );
  };

  inviteTrainer = (request: IInviteTrainerRequest): Promise<Result<IInviteTrainerResponse>> =>
    this.post<IInviteTrainerResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.INVITE_TRAINER(request.organizationId)}`,
      request,
      'organizations.errors.inviteTrainerFailed'
    );

  acceptTrainerInvitation = (
    request: IAcceptTrainerInvitationRequest
  ): Promise<Result<IAcceptTrainerInvitationResponse>> =>
    this.post<IAcceptTrainerInvitationResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.TRAINER_INVITATION_ACCEPT(request.organizationId)}`,
      request,
      'organizations.errors.acceptTrainerInvitationFailed'
    );

  acceptTrainerInvitationPublicByToken = (request: {
    token: string;
  }): Promise<Result<IAcceptTrainerInvitationPublicByTokenResponse>> =>
    this.post<IAcceptTrainerInvitationPublicByTokenResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.INVITATION_ACCEPT_PUBLIC}`,
      request,
      'organizations.errors.acceptTrainerInvitationFailed'
    );

  rejectTrainerInvitationByToken = (request: {
    token: string;
  }): Promise<Result<IRejectTrainerInvitationResponse>> =>
    this.post<IRejectTrainerInvitationResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.INVITATION_REJECT}`,
      request,
      'organizations.errors.rejectTrainerInvitationFailed'
    );

  rejectTrainerInvitation = (
    request: IRejectTrainerInvitationRequest
  ): Promise<Result<IRejectTrainerInvitationResponse>> =>
    this.post<IRejectTrainerInvitationResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.TRAINER_INVITATION_REJECT(request.organizationId)}`,
      request,
      'organizations.errors.rejectTrainerInvitationFailed'
    );

  removeUserFromOrganization = (
    request: IRemoveUserFromOrganizationRequest
  ): Promise<Result<IRemoveUserFromOrganizationResponse>> =>
    this.delete<IRemoveUserFromOrganizationResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_USERS(request.organizationId)}/${request.userId}`,
      null,
      'organizations.errors.removeUserFromOrganizationFailed'
    );

  revokeTrainerRole = (
    request: IRevokeTrainerRoleRequest
  ): Promise<Result<IRevokeTrainerRoleResponse>> =>
    this.post<IRevokeTrainerRoleResponse>(
      `/${ORGANIZATIONS_ENDPOINTS.REVOKE_TRAINER_ROLE(request.organizationId, request.userId)}`,
      {},
      'organizations.errors.revokeTrainerRoleFailed'
    );

  cancelOrganizationInvitation = (
    organizationId: string,
    invitationId: string
  ): Promise<Result<void>> =>
    this.delete<void>(
      `/${ORGANIZATIONS_ENDPOINTS.ORGANIZATION_INVITATION_CANCEL(organizationId, invitationId)}`,
      null,
      'organizations.errors.cancelOrganizationInvitationFailed'
    );
}
